import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { Wrench } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

export interface AvailableServer {
  id: string;
  name: string;
  description: string;
  scope: "global" | "user" | "worker";
  default_on: boolean;
  /** Servers with the same menu group are one switch (e.g. "Internet" = search + fetch). */
  menu_group?: string | null;
}

/** One switch in the tools menu: a server, or all servers of a menu group. */
export interface ToolOption {
  key: string;
  label: string;
  description: string;
  scope: AvailableServer["scope"];
  ids: string[];
}

export function toolOptions(servers: AvailableServer[]): ToolOption[] {
  const out: ToolOption[] = [];
  const groups = new Map<string, ToolOption>();
  for (const s of servers) {
    const group = s.menu_group?.trim();
    if (!group) {
      out.push({ key: s.id, label: s.name, description: s.description, scope: s.scope, ids: [s.id] });
      continue;
    }
    const existing = groups.get(group);
    if (existing) {
      existing.ids.push(s.id);
      existing.description = [existing.description, s.description].filter(Boolean).join(" · ");
    } else {
      const option = { key: `group:${group}`, label: group, description: s.description, scope: s.scope, ids: [s.id] };
      groups.set(group, option);
      out.push(option);
    }
  }
  return out;
}

/** Switches a whole option on or off in the list of server ids. */
export function toggleOption(on: string[], option: ToolOption): string[] {
  const all = option.ids.every((id) => on.includes(id));
  return all ? on.filter((id) => !option.ids.includes(id)) : [...on, ...option.ids.filter((id) => !on.includes(id))];
}

export function useAvailableServers() {
  return useQuery({ queryKey: ["mcp", "available"], queryFn: () => api.get<AvailableServer[]>("/api/mcp/available"), staleTime: 60_000 });
}

/** The MCP servers switched on: the explicit list, or the default ones when the chat has none set. */
export function effectiveServers(value: string[] | null | undefined, servers: AvailableServer[] = []): string[] {
  return value ?? servers.filter((s) => s.default_on).map((s) => s.id);
}

export default function ToolsPicker({ value, onChange }: { value: string[] | null | undefined; onChange: (ids: string[]) => void }) {
  const servers = useAvailableServers().data ?? [];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const on = effectiveServers(value, servers).filter((id) => servers.some((s) => s.id === id));

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!servers.length) return null;
  const options = toolOptions(servers);
  const onCount = options.filter((o) => o.ids.some((id) => on.includes(id))).length;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={clsx(
          "flex items-center gap-1 rounded-lg px-1.5 py-1.5 text-sm",
          onCount ? "text-indigo-600 dark:text-indigo-400" : "text-zinc-500",
          "hover:bg-zinc-100 dark:hover:bg-zinc-800",
        )}
        aria-label="Tools"
        title="Tools (MCP servers)"
      >
        <Wrench size={16} />
        {onCount > 0 && <span className="text-xs tabular-nums">{onCount}</span>}
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-30 mb-2 w-72 rounded-xl border border-zinc-200 bg-white p-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          <p className="px-2 pb-1 text-xs font-medium text-zinc-500">Tools for this chat</p>
          {options.map((o) => (
            <label key={o.key} className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-zinc-50 dark:hover:bg-zinc-800">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-indigo-600"
                checked={o.ids.every((id) => on.includes(id))}
                ref={(el) => {
                  // Partly on (e.g. an older chat with only one server of the group).
                  if (el) el.indeterminate = o.ids.some((id) => on.includes(id)) && !o.ids.every((id) => on.includes(id));
                }}
                onChange={() => onChange(toggleOption(on, o))}
              />
              <span className="min-w-0">
                <span className="block text-sm">
                  {o.label}
                  {o.scope === "user" && <span className="ml-1 text-xs text-zinc-500">(yours)</span>}
                  {o.scope === "worker" && <span className="ml-1 text-xs text-zinc-500">(worker)</span>}
                </span>
                {o.description && <span className="block truncate text-xs text-zinc-500">{o.description}</span>}
              </span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
